// frontend/src/pages/OwnerEditOffer.js
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../config";
import { safeGetItem } from "../utils/storage";

export default function OwnerEditOffer() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const token = safeGetItem("token");

  const [consultancy, setConsultancy] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [discount, setDiscount] = useState("");
  const [validTill, setValidTill] = useState("");
  const [boosting, setBoosting] = useState({
    highlightOnHomepage: false,
    sendToStudents: false,
    promoteOnTop: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      if (!offerId) {
        alert("Invalid offer");
        navigate("/owner/dashboard");
        return;
      }

      try {
        const res = await fetch(`${BACKEND_URL}/consultancies/owner/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          alert("Unable to load consultancy");
          navigate("/owner/dashboard");
          return;
        }

        const data = await res.json();
        setConsultancy(data);

        const offers = Array.isArray(data.offers) ? data.offers : [];
        const offer = offers.find((item) => item._id === offerId);

        if (!offer) {
          alert("Offer not found");
          navigate("/owner/dashboard");
          return;
        }

        setTitle(offer.title || "");
        setDescription(offer.description || "");
        setDiscount(offer.discount || "");
        setValidTill(
          offer.validTill
            ? new Date(offer.validTill).toISOString().slice(0, 10)
            : ""
        );
        setBoosting({
          highlightOnHomepage: Boolean(offer.highlightOnHomepage),
          sendToStudents: Boolean(offer.sendToStudents),
          promoteOnTop: Boolean(offer.promoteOnTop),
        });
      } catch (err) {
        console.error(err);
        alert("Could not load offer");
        navigate("/owner/dashboard");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [offerId, navigate, token]);

  const handleBoostToggle = (field) => {
    setBoosting((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!consultancy) return;

    setSaving(true);

    try {
      const res = await fetch(
        `${BACKEND_URL}/consultancies/${consultancy._id}/offers/${offerId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title,
            description,
            discount,
            validTill: validTill || null,
            highlightOnHomepage: boosting.highlightOnHomepage,
            sendToStudents: boosting.sendToStudents,
            promoteOnTop: boosting.promoteOnTop,
          }),
        }
      );

      if (res.ok) {
        alert("Offer updated successfully");
        navigate("/owner/dashboard");
      } else {
        alert("Failed to update offer");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving offer");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!consultancy) return;
    if (!window.confirm("Delete this offer?")) return;

    setSaving(true);

    try {
      const res = await fetch(
        `${BACKEND_URL}/consultancies/${consultancy._id}/offers/${offerId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.ok) {
        alert("Offer deleted");
        navigate("/owner/dashboard");
      } else {
        alert("Failed to delete");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting offer");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-700">
        Loading offer...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50 p-6">
      <div className="max-w-xl mx-auto bg-white rounded-xl shadow p-6">
        <h1 className="text-2xl font-bold mb-4 text-blue-700">
          Edit Offer
        </h1>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">
              Title
            </label>
            <input
              className="w-full border rounded px-3 py-2"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Free Flight Ticket"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Description
            </label>
            <textarea
              className="w-full border rounded px-3 py-2"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description about the offer"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Discount text
            </label>
            <input
              className="w-full border rounded px-3 py-2"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="100% off / Flat ₹10,000 off"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Valid Till
            </label>
            <input
              type="date"
              className="w-full border rounded px-3 py-2"
              value={validTill}
              onChange={(e) => setValidTill(e.target.value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex items-center gap-2 rounded-xl border border-slate-200/60 bg-slate-50 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={boosting.highlightOnHomepage}
                onChange={() => handleBoostToggle("highlightOnHomepage")}
              />
              Highlight on homepage
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-slate-200/60 bg-slate-50 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={boosting.sendToStudents}
                onChange={() => handleBoostToggle("sendToStudents")}
              />
              Send to students
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-slate-200/60 bg-slate-50 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={boosting.promoteOnTop}
                onChange={() => handleBoostToggle("promoteOnTop")}
              />
              Promote on top
            </label>
          </div>

          <div className="flex justify-between items-center pt-4">
            <button
              type="button"
              onClick={() => navigate("/owner/dashboard")}
              className="px-4 py-2 rounded border border-gray-300"
              disabled={saving}
            >
              Cancel
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 rounded bg-red-600 text-white"
                disabled={saving}
              >
                Delete
              </button>

              <button
                type="submit"
                className="px-4 py-2 rounded bg-blue-600 text-white"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
